/**
 * Main navigation items configuration.
 *
 * Each item can have:
 * - label: Display text (required)
 * - href: Link URL (required for regular items, optional for dropdowns)
 * - urlPrefix: URL path prefix for startsWith matching against window.location.pathname (preferred)
 * - activeUrls: Array of URL paths when multiple unrelated URLs need to match
 * - pathPrefix: Controller path for startsWith matching against store.currentPath (legacy fallback)
 * - activePaths: Array of controller paths when multiple unrelated paths need to match (legacy fallback)
 * - show: Function receiving store to determine visibility
 * - items: Array of dropdown items (makes this a dropdown). Each sub-item supports the same
 *   matching/visibility fields as a top-level item. The dropdown itself is auto-hidden when
 *   every sub-item is filtered out by `show`.
 * - dynamicLabel: Function receiving store to compute label dynamically
 *
 * URL-based matching (urlPrefix/activeUrls) is checked first. If no URL fields are present,
 * falls back to controller-path matching (pathPrefix/activePaths).
 */

const bulkPostingShow = (store) => {
  if (!store.user.editor) return false
  const isLinda = store.wlc.appCodename === 'linda'
  if (isLinda && !store.organization.allowBulkPostingForLinda) return false
  return true
}

export default [
  {
    label: 'Locations',
    href: '/locations',
    pathPrefix: 'locations'
  },
  {
    label: 'Reviews',
    href: '/reviews_dashboard',
    urlPrefix: '/reviews_dashboard',
    show: (store) => store.hasFeature('reviews_dashboard')
  },
  {
    label: 'Content',
    items: [
      {
        label: 'Multi Media',
        href: '/gmb/multi_location_media',
        pathPrefix: 'gmb/multi_location_media',
        show: bulkPostingShow
      },
      {
        label: 'Multi Posts',
        href: '/gmb/multi_location_posts',
        pathPrefix: 'gmb/multi_location_posts',
        show: bulkPostingShow
      },
      {
        label: 'Bulk Posts',
        href: '/gmb/local_posts/bulk_posts',
        pathPrefix: 'gmb/local_posts/bulk_posts',
        show: bulkPostingShow
      },
      {
        label: 'Gallery',
        href: '/gallery',
        activePaths: ['galleries', 'media_schedules'],
        show: (store) => !store.user.viewer
      }
    ]
  },
  {
    label: 'Growth',
    items: [
      {
        label: 'GeoGrid Search',
        href: '/scheduled_geogrids',
        // URL-based matching stays correct under client-side routing, where
        // controller-path (activePaths) goes stale
        activeUrls: ['/geogrids', '/scheduled_geogrids']
      },
      {
        label: 'MLAP',
        href: '/mlap',
        urlPrefix: '/mlap',
        show: (store) => store.hasFeature('mlap')
      },
      {
        label: 'Expansion IQ',
        href: '/expansion/overview',
        urlPrefix: '/expansion/',
        show: (store) => store.hasFeature('expansion_iq')
      }
    ]
  },
  {
    label: 'Reports',
    href: '/reports',
    // URL-based matching stays correct under client-side routing, where
    // controller-path (activePaths) goes stale. /reports also covers
    // /reports/configurations; /audit_report_configurations also covers the
    // nested audit report show.
    activeUrls: ['/reports', '/audit_reports', '/audit_report_configurations'],
    show: (store) => !store.user.viewer
  },
  {
    label: 'GeoBooster',
    href: '/gb',
    urlPrefix: '/gb/',
    show: (store) => !store.user.viewer && store.hasFeature('gb'),
    dynamicLabel: (store) => store.wlc.gbName
  },
  {
    label: 'Tracking',
    items: [
      {
        label: 'Call Tracking',
        href: '/call_tracking/incoming_calls',
        urlPrefix: '/call_tracking/',
        show: (store) => !store.user.viewer && store.hasFeature('jensen')
      },
      {
        label: 'Form Tracking',
        href: '/form_tracking/widgets',
        urlPrefix: '/form_tracking/',
        show: (store) => !store.user.viewer && store.hasFeature('form_tracking')
      },
      {
        label: 'Attribution',
        href: '/attribution/widgets',
        pathPrefix: 'attribution',
        show: (store) => !store.user.viewer && store.hasFeature('attribution')
      }
    ]
  },
  {
    label: 'LSA',
    href: '/lsa/dashboard',
    urlPrefix: '/lsa/',
    pathPrefix: 'lsa',
    show: (store) => store.hasFeature('lsa')
  }
]
